import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import AppShell from "../components/AppShell";
import { Button } from "../components/ui/button";
import {
    Card, CardContent, CardHeader, CardTitle,
} from "../components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
    ShieldAlert, ShieldCheck, LogOut, Ban, FlagTriangleRight,
    CheckCircle2, XCircle, ArrowUpRight, Loader2, RefreshCcw,
    Package, ExternalLink, Play,
} from "lucide-react";

/**
 * Iter76 — Admin Security Dashboard.
 * Admin-only (backend enforces 403; nav item hidden for other roles).
 *
 * Five panels:
 *   1. Summary hero row (counts).
 *   2. Live Red Flags (last 50 with filters + row actions).
 *   3. Active Sessions (force-logout row action).
 *   4. Recent Failed Logins (+ top-IPs sub-panel).
 *   5. IP Blocklist + Alert Settings (compact form).
 */

const SEVERITY_CLASS = {
    info: "bg-slate-100 text-slate-700 border-slate-200",
    warning: "bg-amber-100 text-amber-800 border-amber-200",
    critical: "bg-rose-100 text-rose-800 border-rose-200",
};

const FLAG_LABEL = {
    new_device: "New device",
    new_country: "New country",
    impossible_travel: "Impossible travel",
    brute_force_ip: "Brute force",
    bulk_download: "Bulk download",
};

function fmtDate(s) {
    if (!s) return "—";
    try {
        const d = typeof s === "string" ? new Date(s) : s;
        return d.toLocaleString();
    } catch { return String(s); }
}

function SummaryRow({ summary }) {
    if (!summary) return null;
    const tiles = [
        { label: "New Events (24h)", value: summary.new_events_24h, testid: "sum-events" },
        { label: "Critical (unacked)", value: summary.critical_new, testid: "sum-critical" },
        { label: "IPs Blocked", value: summary.active_ip_blocks, testid: "sum-blocks" },
        { label: "Failed Logins (24h)", value: summary.failed_logins_24h, testid: "sum-failed" },
        { label: "Active Sessions", value: summary.active_sessions, testid: "sum-sessions" },
    ];
    return (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
            {tiles.map((t) => (
                <Card key={t.testid} data-testid={t.testid}>
                    <CardContent className="p-4">
                        <div className="text-xs text-slate-500 uppercase tracking-wider">{t.label}</div>
                        <div className="text-3xl font-semibold text-slate-900 mt-1">{t.value ?? 0}</div>
                    </CardContent>
                </Card>
            ))}
        </div>
    );
}

function RedFlagsPanel({ events, onAction, filters, setFilters, loading, onRefresh }) {
    return (
        <Card data-testid="panel-red-flags">
            <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                    <ShieldAlert className="w-4 h-4 text-rose-600" />
                    Red Flags — last 50
                </CardTitle>
                <div className="flex items-center gap-2">
                    <Select value={filters.type || "all"} onValueChange={(v) => setFilters({ ...filters, type: v === "all" ? "" : v })}>
                        <SelectTrigger className="w-40 h-8" data-testid="filter-type">
                            <SelectValue placeholder="All types" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All types</SelectItem>
                            {Object.entries(FLAG_LABEL).map(([k, v]) => (
                                <SelectItem key={k} value={k}>{v}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Select value={filters.severity || "all"} onValueChange={(v) => setFilters({ ...filters, severity: v === "all" ? "" : v })}>
                        <SelectTrigger className="w-32 h-8" data-testid="filter-severity">
                            <SelectValue placeholder="All" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All</SelectItem>
                            <SelectItem value="info">Info</SelectItem>
                            <SelectItem value="warning">Warning</SelectItem>
                            <SelectItem value="critical">Critical</SelectItem>
                        </SelectContent>
                    </Select>
                    <Select value={filters.status || "all"} onValueChange={(v) => setFilters({ ...filters, status: v === "all" ? "" : v })}>
                        <SelectTrigger className="w-32 h-8" data-testid="filter-status">
                            <SelectValue placeholder="All" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All</SelectItem>
                            <SelectItem value="new">New</SelectItem>
                            <SelectItem value="acknowledged">Acknowledged</SelectItem>
                            <SelectItem value="dismissed">Dismissed</SelectItem>
                            <SelectItem value="escalated">Escalated</SelectItem>
                        </SelectContent>
                    </Select>
                    <Button size="sm" variant="ghost" onClick={onRefresh} data-testid="refresh-events">
                        <RefreshCcw className="w-3.5 h-3.5" />
                    </Button>
                </div>
            </CardHeader>
            <CardContent className="p-0">
                {loading ? (
                    <div className="p-6 text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
                ) : events.length === 0 ? (
                    <div className="p-6 text-slate-500 text-sm" data-testid="events-empty">No events match the current filters.</div>
                ) : (
                    <div className="overflow-auto max-h-[520px]">
                        <table className="w-full text-sm">
                            <thead className="sticky top-0 bg-slate-50 text-xs uppercase text-slate-500">
                                <tr>
                                    <th className="text-left px-3 py-2">Time</th>
                                    <th className="text-left px-3 py-2">Type</th>
                                    <th className="text-left px-3 py-2">User</th>
                                    <th className="text-left px-3 py-2">IP / Country</th>
                                    <th className="text-left px-3 py-2">Device</th>
                                    <th className="text-left px-3 py-2">Status</th>
                                    <th className="text-right px-3 py-2">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {events.map((ev) => (
                                    <tr key={ev.id} className="border-t" data-testid={`event-${ev.id}`}>
                                        <td className="px-3 py-2 text-slate-500 whitespace-nowrap">{fmtDate(ev.at)}</td>
                                        <td className="px-3 py-2">
                                            <span className={`inline-block text-[11px] px-2 py-0.5 rounded border ${SEVERITY_CLASS[ev.severity]}`}>
                                                {FLAG_LABEL[ev.event_type] || ev.event_type}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2 text-slate-700">{ev.details?.email || ev.user_id || "—"}</td>
                                        <td className="px-3 py-2 font-mono text-xs">{ev.ip || "—"}{ev.country ? ` · ${ev.country}` : ""}</td>
                                        <td className="px-3 py-2 text-slate-600">{ev.ua_summary || "—"}</td>
                                        <td className="px-3 py-2"><Badge variant={ev.status === "new" ? "destructive" : "outline"}>{ev.status}</Badge></td>
                                        <td className="px-3 py-2 text-right whitespace-nowrap">
                                            {ev.status === "new" && (
                                                <>
                                                    <Button size="sm" variant="ghost" onClick={() => onAction(ev.id, "acknowledge")} data-testid={`ack-${ev.id}`}>
                                                        <CheckCircle2 className="w-3.5 h-3.5" />
                                                    </Button>
                                                    <Button size="sm" variant="ghost" onClick={() => onAction(ev.id, "dismiss")} data-testid={`dismiss-${ev.id}`}>
                                                        <XCircle className="w-3.5 h-3.5" />
                                                    </Button>
                                                    <Button size="sm" variant="ghost" onClick={() => onAction(ev.id, "escalate")} data-testid={`escalate-${ev.id}`}>
                                                        <ArrowUpRight className="w-3.5 h-3.5" />
                                                    </Button>
                                                </>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

function SessionsPanel() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get("/admin/security/sessions");
            setRows(r.data?.items || []);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const revoke = async (jti) => {
        try {
            await api.post(`/admin/security/sessions/${jti}/revoke`);
            toast.success("Session revoked");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <Card data-testid="panel-sessions">
            <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4 text-slate-600" />
                    Active Sessions
                </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
                {loading ? <div className="p-6 text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>
                : rows.length === 0 ? <div className="p-6 text-slate-500 text-sm" data-testid="sessions-empty">No active sessions.</div>
                : (
                    <div className="overflow-auto max-h-[420px]">
                        <table className="w-full text-sm">
                            <thead className="sticky top-0 bg-slate-50 text-xs uppercase text-slate-500">
                                <tr>
                                    <th className="text-left px-3 py-2">User</th>
                                    <th className="text-left px-3 py-2">Role</th>
                                    <th className="text-left px-3 py-2">IP · Country</th>
                                    <th className="text-left px-3 py-2">Device</th>
                                    <th className="text-left px-3 py-2">Last seen</th>
                                    <th className="text-right px-3 py-2">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r) => (
                                    <tr key={r.jti} className="border-t" data-testid={`session-${r.jti}`}>
                                        <td className="px-3 py-2 text-slate-700">{r.name || r.email}</td>
                                        <td className="px-3 py-2 text-xs text-slate-500">{r.role}</td>
                                        <td className="px-3 py-2 font-mono text-xs">{r.ip || "—"}{r.country ? ` · ${r.country}` : ""}</td>
                                        <td className="px-3 py-2 text-slate-600">{r.ua_summary || "—"}</td>
                                        <td className="px-3 py-2 text-slate-500 whitespace-nowrap">{fmtDate(r.last_seen_at)}</td>
                                        <td className="px-3 py-2 text-right">
                                            <Button size="sm" variant="ghost" onClick={() => revoke(r.jti)} data-testid={`force-logout-${r.jti}`}>
                                                <LogOut className="w-3.5 h-3.5" />
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

function FailedLoginsPanel() {
    const [rows, setRows] = useState([]);
    const [ips, setIps] = useState([]);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const [a, b] = await Promise.all([
                    api.get("/admin/security/failed-logins?limit=100"),
                    api.get("/admin/security/top-ips?since_hours=24"),
                ]);
                setRows(a.data?.items || []);
                setIps(b.data?.items || []);
            } catch (e) { toast.error(formatApiError(e)); }
            finally { setLoading(false); }
        })();
    }, []);

    const blockIp = async (ip) => {
        try {
            await api.post("/admin/security/ip-blocklist", { ip, reason: "manual dashboard block", hours: 24 });
            toast.success(`IP ${ip} blocked for 24h`);
        } catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <div className="grid md:grid-cols-2 gap-4">
            <Card data-testid="panel-failed-logins">
                <CardHeader><CardTitle className="text-base">Recent Failed Logins</CardTitle></CardHeader>
                <CardContent className="p-0">
                    {loading ? <div className="p-6 text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>
                    : rows.length === 0 ? <div className="p-6 text-slate-500 text-sm">No failed logins.</div>
                    : (
                        <div className="overflow-auto max-h-[420px]">
                            <table className="w-full text-sm">
                                <thead className="sticky top-0 bg-slate-50 text-xs uppercase text-slate-500">
                                    <tr>
                                        <th className="text-left px-3 py-2">Time</th>
                                        <th className="text-left px-3 py-2">Email</th>
                                        <th className="text-left px-3 py-2">IP · Country</th>
                                        <th className="text-left px-3 py-2">Reason</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((r, i) => (
                                        <tr key={i} className="border-t">
                                            <td className="px-3 py-2 text-slate-500 whitespace-nowrap">{fmtDate(r.at)}</td>
                                            <td className="px-3 py-2 text-slate-700">{r.email}</td>
                                            <td className="px-3 py-2 font-mono text-xs">{r.ip || "—"}{r.country ? ` · ${r.country}` : ""}</td>
                                            <td className="px-3 py-2 text-xs text-slate-500">{r.reason}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>
            <Card data-testid="panel-top-ips">
                <CardHeader><CardTitle className="text-base">Top IPs (24h)</CardTitle></CardHeader>
                <CardContent className="p-0">
                    {ips.length === 0 ? <div className="p-6 text-slate-500 text-sm">No suspicious IP activity.</div>
                    : (
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                                <tr>
                                    <th className="text-left px-3 py-2">IP</th>
                                    <th className="text-left px-3 py-2">Country</th>
                                    <th className="text-right px-3 py-2">Fails</th>
                                    <th className="text-right px-3 py-2">Emails</th>
                                    <th className="text-right px-3 py-2"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {ips.map((r, i) => (
                                    <tr key={i} className="border-t" data-testid={`top-ip-${r.ip}`}>
                                        <td className="px-3 py-2 font-mono text-xs">{r.ip || "—"}</td>
                                        <td className="px-3 py-2 text-xs text-slate-500">{r.country || "—"}</td>
                                        <td className="px-3 py-2 text-right">{r.count}</td>
                                        <td className="px-3 py-2 text-right">{r.distinct_emails}</td>
                                        <td className="px-3 py-2 text-right">
                                            <Button size="sm" variant="ghost" onClick={() => blockIp(r.ip)} data-testid={`block-ip-${r.ip}`}>
                                                <Ban className="w-3.5 h-3.5" />
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}

function BlocklistPanel() {
    const [rows, setRows] = useState([]);
    const load = async () => {
        try {
            const r = await api.get("/admin/security/ip-blocklist");
            setRows(r.data?.items || []);
        } catch (e) { toast.error(formatApiError(e)); }
    };
    useEffect(() => { load(); }, []);
    const remove = async (ip) => {
        try {
            await api.delete(`/admin/security/ip-blocklist/${encodeURIComponent(ip)}`);
            toast.success(`IP ${ip} unblocked`);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <Card data-testid="panel-blocklist">
            <CardHeader><CardTitle className="text-base">IP Blocklist</CardTitle></CardHeader>
            <CardContent className="p-0">
                {rows.length === 0 ? <div className="p-6 text-slate-500 text-sm" data-testid="blocklist-empty">No IPs currently blocked.</div>
                : (
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                            <tr>
                                <th className="text-left px-3 py-2">IP</th>
                                <th className="text-left px-3 py-2">Reason</th>
                                <th className="text-left px-3 py-2">Blocked</th>
                                <th className="text-left px-3 py-2">Expires</th>
                                <th className="text-right px-3 py-2"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r, i) => (
                                <tr key={i} className="border-t" data-testid={`block-${r.ip}`}>
                                    <td className="px-3 py-2 font-mono text-xs">{r.ip}</td>
                                    <td className="px-3 py-2 text-slate-600">{r.reason}</td>
                                    <td className="px-3 py-2 text-slate-500 whitespace-nowrap">{fmtDate(r.blocked_at)}</td>
                                    <td className="px-3 py-2 text-slate-500 whitespace-nowrap">{fmtDate(r.expires_at)}</td>
                                    <td className="px-3 py-2 text-right">
                                        <Button size="sm" variant="ghost" onClick={() => remove(r.ip)} data-testid={`unblock-${r.ip}`}>
                                            Unblock
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </CardContent>
        </Card>
    );
}

function SettingsPanel() {
    const [settings, setSettings] = useState(null);
    useEffect(() => {
        (async () => {
            try { const r = await api.get("/admin/security/settings"); setSettings(r.data); }
            catch (e) { toast.error(formatApiError(e)); }
        })();
    }, []);
    const save = async () => {
        try {
            const r = await api.put("/admin/security/settings", settings);
            setSettings(r.data);
            toast.success("Settings saved");
        } catch (e) { toast.error(formatApiError(e)); }
    };
    if (!settings) return null;
    const flags = Object.keys(settings.enabled_flags || {});
    return (
        <Card data-testid="panel-settings">
            <CardHeader><CardTitle className="text-base">Alert Settings</CardTitle></CardHeader>
            <CardContent className="space-y-4">
                <div className="text-xs text-slate-500">Toggle each flag type on/off, set severity, and throttle emails to admins.</div>
                <table className="w-full text-sm">
                    <thead className="text-xs uppercase text-slate-500">
                        <tr>
                            <th className="text-left py-2">Flag</th>
                            <th className="text-left py-2">Enabled</th>
                            <th className="text-left py-2">Severity</th>
                            <th className="text-left py-2">Email throttle (min)</th>
                        </tr>
                    </thead>
                    <tbody>
                        {flags.map((f) => (
                            <tr key={f} className="border-t">
                                <td className="py-2">{FLAG_LABEL[f] || f}</td>
                                <td className="py-2">
                                    <Switch
                                        checked={!!settings.enabled_flags[f]}
                                        onCheckedChange={(v) => setSettings({
                                            ...settings, enabled_flags: { ...settings.enabled_flags, [f]: v },
                                        })}
                                        data-testid={`toggle-${f}`}
                                    />
                                </td>
                                <td className="py-2">
                                    <Select
                                        value={settings.severities[f]}
                                        onValueChange={(v) => setSettings({
                                            ...settings, severities: { ...settings.severities, [f]: v },
                                        })}>
                                        <SelectTrigger className="w-32 h-8" data-testid={`sev-${f}`}><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="info">info</SelectItem>
                                            <SelectItem value="warning">warning</SelectItem>
                                            <SelectItem value="critical">critical</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </td>
                                <td className="py-2">
                                    <Input
                                        type="number"
                                        min={0}
                                        value={settings.email_throttle_minutes[f] ?? 0}
                                        onChange={(e) => setSettings({
                                            ...settings,
                                            email_throttle_minutes: {
                                                ...settings.email_throttle_minutes,
                                                [f]: Number(e.target.value) || 0,
                                            },
                                        })}
                                        className="w-24 h-8"
                                        data-testid={`throttle-${f}`}
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <div>
                    <Label className="text-xs">Secondary admin emails (comma-separated)</Label>
                    <Input
                        value={(settings.secondary_admin_emails || []).join(", ")}
                        onChange={(e) => setSettings({
                            ...settings, secondary_admin_emails: e.target.value.split(",").map(s => s.trim()).filter(Boolean),
                        })}
                        placeholder="security@aslwms.com, on-call@aslwms.com"
                        className="mt-1"
                        data-testid="secondary-emails-input"
                    />
                </div>
                <Button onClick={save} data-testid="save-settings">Save settings</Button>
            </CardContent>
        </Card>
    );
}

export default function AdminSecurity() {
    const [summary, setSummary] = useState(null);
    const [events, setEvents] = useState([]);
    const [filters, setFilters] = useState({ type: "", severity: "", status: "" });
    const [eventsLoading, setEventsLoading] = useState(true);

    const loadEvents = async () => {
        setEventsLoading(true);
        try {
            const qp = new URLSearchParams();
            if (filters.type) qp.append("type", filters.type);
            if (filters.severity) qp.append("severity", filters.severity);
            if (filters.status) qp.append("status", filters.status);
            qp.append("limit", "50");
            const r = await api.get(`/admin/security/events?${qp.toString()}`);
            setEvents(r.data?.items || []);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setEventsLoading(false); }
    };

    const loadSummary = async () => {
        try {
            const r = await api.get("/admin/security/summary");
            setSummary(r.data);
        } catch (e) { toast.error(formatApiError(e)); }
    };

    useEffect(() => { loadSummary(); }, []);
    useEffect(() => { loadEvents(); /* eslint-disable-line */ }, [filters.type, filters.severity, filters.status]);

    const onAction = async (id, action) => {
        try {
            await api.post(`/admin/security/events/${id}/${action}`, {});
            toast.success(`Event ${action}d`);
            loadEvents(); loadSummary();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <AppShell title="Security Dashboard">
            <div data-testid="admin-security-page" className="space-y-6">
                <SummaryRow summary={summary} />

                <Tabs defaultValue="flags" className="w-full">
                    <TabsList data-testid="security-tabs">
                        <TabsTrigger value="flags" data-testid="tab-flags">Red Flags</TabsTrigger>
                        <TabsTrigger value="sessions" data-testid="tab-sessions">Sessions</TabsTrigger>
                        <TabsTrigger value="logins" data-testid="tab-logins">Failed Logins</TabsTrigger>
                        <TabsTrigger value="blocklist" data-testid="tab-blocklist">IP Blocklist</TabsTrigger>
                        <TabsTrigger value="dependencies" data-testid="tab-dependencies">Dependencies</TabsTrigger>
                        <TabsTrigger value="settings" data-testid="tab-settings">Settings</TabsTrigger>
                    </TabsList>
                    <TabsContent value="flags" className="mt-4">
                        <RedFlagsPanel
                            events={events} onAction={onAction}
                            filters={filters} setFilters={setFilters}
                            loading={eventsLoading} onRefresh={loadEvents}
                        />
                    </TabsContent>
                    <TabsContent value="sessions" className="mt-4"><SessionsPanel /></TabsContent>
                    <TabsContent value="logins" className="mt-4"><FailedLoginsPanel /></TabsContent>
                    <TabsContent value="blocklist" className="mt-4"><BlocklistPanel /></TabsContent>
                    <TabsContent value="dependencies" className="mt-4"><DependenciesPanel /></TabsContent>
                    <TabsContent value="settings" className="mt-4"><SettingsPanel /></TabsContent>
                </Tabs>
            </div>
        </AppShell>
    );
}


// ---------------------------------------------------------------------------
// Iter79 — Dependencies panel (pip-audit + yarn audit)
// ---------------------------------------------------------------------------
function DependenciesPanel() {
    const [latest, setLatest] = useState(null);
    const [loading, setLoading] = useState(true);
    const [running, setRunning] = useState(false);
    const [severityFilter, setSeverityFilter] = useState("all");

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get("/admin/security/dependency-scans/latest");
            setLatest(r.data || {});
        } catch (e) {
            toast.error(formatApiError(e));
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const runNow = async () => {
        setRunning(true);
        try {
            const r = await api.post("/admin/security/dependency-scans/run-now");
            const pip = r.data.pip?.summary || {};
            const npm = r.data.npm?.summary || {};
            toast.success(`Scan complete — pip: ${pip.critical || 0}c/${pip.high || 0}h  npm: ${npm.critical || 0}c/${npm.high || 0}h`);
            await load();
        } catch (e) {
            const detail = e?.response?.data?.detail;
            if (detail?.error === "cooldown") {
                toast.error(`Cool-down: try again in ${Math.ceil(detail.retry_after_seconds)}s`);
            } else {
                toast.error(formatApiError(e));
            }
        } finally { setRunning(false); }
    };

    const sevBadge = (sev) => {
        const cls = {
            critical: "bg-rose-100 text-rose-800 border-rose-300",
            high: "bg-orange-100 text-orange-800 border-orange-300",
            medium: "bg-amber-100 text-amber-800 border-amber-300",
            low: "bg-slate-100 text-slate-700 border-slate-300",
        }[sev] || "bg-slate-100 text-slate-700 border-slate-300";
        return <span className={`text-[10px] px-2 py-0.5 rounded-full border font-semibold uppercase ${cls}`}>{sev}</span>;
    };

    const StaleWarning = ({ at }) => {
        if (!at) return null;
        const ageHours = (Date.now() - new Date(at).getTime()) / 3600_000;
        if (ageHours < 48) return null;
        return (
            <span
                className="text-xs px-2 py-1 rounded-full bg-orange-50 border border-orange-200 text-orange-800 inline-flex items-center gap-1"
                data-testid="deps-stale-warning"
            >
                <ShieldAlert className="h-3 w-3" /> Last scan {ageHours.toFixed(0)}h old
            </span>
        );
    };

    const renderSourceBlock = (src) => {
        const scan = latest?.[src];
        if (!scan) return (
            <Card><CardHeader><CardTitle className="text-sm">{src.toUpperCase()}</CardTitle></CardHeader>
                <CardContent><div className="text-xs text-slate-500">No scan yet.</div></CardContent></Card>
        );
        const vulns = (scan.vulnerabilities || []).filter(v =>
            severityFilter === "all" ? true : v.severity === severityFilter
        );
        return (
            <Card data-testid={`deps-source-${src}`}>
                <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                        <CardTitle className="text-sm flex items-center gap-2">
                            <Package className="h-4 w-4" /> {src === "pip" ? "Python (pip-audit)" : "Frontend (yarn/npm audit)"}
                            {src === "npm" && scan.scanner_used && (
                                <span
                                    className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 border border-slate-200 text-slate-600 font-medium uppercase"
                                    data-testid="deps-scanner-used"
                                    title="Which frontend scanner was used for this scan"
                                >
                                    via {scan.scanner_used}
                                </span>
                            )}
                        </CardTitle>
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                            <span>{new Date(scan.at).toLocaleString()}</span>
                            <StaleWarning at={scan.at} />
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="space-y-3">
                    {/* Severity chips */}
                    <div className="flex items-center gap-2 text-xs">
                        {["critical", "high", "medium", "low"].map(s => (
                            <span key={s} className={`px-2 py-1 rounded-md border font-semibold ${
                                (scan.summary?.[s] || 0) > 0
                                    ? {
                                        critical: "bg-rose-50 border-rose-300 text-rose-800",
                                        high: "bg-orange-50 border-orange-300 text-orange-800",
                                        medium: "bg-amber-50 border-amber-300 text-amber-800",
                                        low: "bg-slate-100 border-slate-300 text-slate-700",
                                    }[s]
                                    : "bg-slate-50 border-slate-200 text-slate-400"
                            }`} data-testid={`deps-summary-${src}-${s}`}>
                                {scan.summary?.[s] || 0} {s}
                            </span>
                        ))}
                        {scan.new_findings_count > 0 && (
                            <Badge className="bg-rose-600 text-white">{scan.new_findings_count} new since last scan</Badge>
                        )}
                        {scan.error && (
                            <Badge className="bg-slate-700 text-white">error: {scan.error}</Badge>
                        )}
                    </div>
                    {/* Vulnerability table */}
                    {vulns.length === 0 ? (
                        <div className="text-xs text-slate-500 py-4 text-center">
                            {scan.summary && (scan.summary.critical + scan.summary.high + scan.summary.medium + scan.summary.low) === 0
                                ? "✅ No vulnerabilities detected in this scan."
                                : "No vulnerabilities match the current filter."}
                        </div>
                    ) : (
                        <div className="overflow-x-auto border rounded">
                            <table className="w-full text-xs" data-testid={`deps-table-${src}`}>
                                <thead className="bg-slate-50">
                                    <tr>
                                        <th className="text-left px-3 py-2">Package</th>
                                        <th className="text-left px-3 py-2">Installed</th>
                                        <th className="text-left px-3 py-2">Fixed in</th>
                                        <th className="text-left px-3 py-2">Severity</th>
                                        <th className="text-left px-3 py-2">Advisory</th>
                                        <th className="text-left px-3 py-2">Description</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {vulns.slice(0, 100).map((v, i) => (
                                        <tr key={`${v.package}-${v.cve_id}-${i}`} className="border-t hover:bg-slate-50">
                                            <td className="px-3 py-2 font-mono text-[11px]">{v.package}</td>
                                            <td className="px-3 py-2 font-mono text-[11px]">{v.installed_version || "-"}</td>
                                            <td className="px-3 py-2 font-mono text-[11px]">{(v.fixed_versions || []).join(", ") || "—"}</td>
                                            <td className="px-3 py-2">{sevBadge(v.severity)}</td>
                                            <td className="px-3 py-2">
                                                {v.advisory_url ? (
                                                    <a href={v.advisory_url} target="_blank" rel="noreferrer"
                                                        className="text-blue-600 hover:underline inline-flex items-center gap-1"
                                                        data-testid={`deps-advisory-link-${i}`}>
                                                        {v.cve_id || "link"} <ExternalLink className="h-3 w-3" />
                                                    </a>
                                                ) : (v.cve_id || "-")}
                                            </td>
                                            <td className="px-3 py-2 text-slate-600 max-w-lg truncate" title={v.description}>
                                                {v.description || ""}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>
        );
    };

    return (
        <div className="space-y-4" data-testid="deps-panel">
            {/* Toolbar */}
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                    <Label className="text-xs">Filter severity:</Label>
                    <Select value={severityFilter} onValueChange={setSeverityFilter}>
                        <SelectTrigger className="w-40 h-8 text-xs" data-testid="deps-severity-filter">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All severities</SelectItem>
                            <SelectItem value="critical">Critical</SelectItem>
                            <SelectItem value="high">High</SelectItem>
                            <SelectItem value="medium">Medium</SelectItem>
                            <SelectItem value="low">Low</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={load} disabled={loading}
                        data-testid="deps-refresh">
                        <RefreshCcw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
                    </Button>
                    <Button className="asl-navy-bg text-white" size="sm" onClick={runNow} disabled={running}
                        data-testid="deps-run-now">
                        {running ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Play className="h-4 w-4 mr-1" />}
                        Run scan now
                    </Button>
                </div>
            </div>
            {renderSourceBlock("pip")}
            {renderSourceBlock("npm")}
        </div>
    );
}
